import { Box } from '@material-ui/core';
import { useBaseStore, useMiscellaneousRoutes } from '@roc/feature-app-core';
import {
  ChatRoomType,
  FilterType,
  useCommunicationRoutesConfig,
  useLoanCommunicationStore,
} from '@roc/feature-communication';
import { LoanProcess } from '@roc/feature-utils';
import { BreadcrumbsNew } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useBorrowerLoanApplicationsStore } from '../../hooks/useBorrowerLoanApplicationsStore';
import { BorrowerLoanApplicationDetailsChildRoutes } from './components/borrowerLoanApplicationDetailsChildRoutes';
import { useBorrowerLoanApplicationsRoutes } from '../../hooks/useBorrowerLoanApplicationsRoutes';
import { BorrowerLoanDetailsFAB } from './components/borrowerLoanDetailsFAB';
import { useDocumentStore } from '@roc/feature-documents';

export const BorrowerLoanApplicationDetailsV3 = observer(() => {
  const { loanId } = useParams<{ loanId: string }>();
  const [
    doesUserHasAnyConversations,
    setDoesUserHasAnyConversations,
  ] = useState<boolean>(false);
  const { borrowerLoanApplicationsStore } = useBorrowerLoanApplicationsStore();
  const {
    borrowerLoanApplicationRoutesConfig: borrowerLoanRoutesConfig,
  } = useBorrowerLoanApplicationsRoutes();
  const { communicationRoutesConfig } = useCommunicationRoutesConfig();
  const { loanCommunicationStore } = useLoanCommunicationStore();
  const { miscellaneousRoutesConfig } = useMiscellaneousRoutes();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { loanDetails } = borrowerLoanApplicationsStore;
  const { globalStore } = useBaseStore();
  const { documentStore } = useDocumentStore();
  const {
    breadCrumbsStore: { breadCrumbItems },
  } = globalStore;
  const [conversationSidToUse, setConversationSidToUse] = useState<string>();

  const tabRouteUrls: Record<string, string> = {
    'loan-summary': borrowerLoanRoutesConfig.loans(loanId).children.dashboard
      .url,
    tasks: borrowerLoanRoutesConfig.loans(loanId).children.borrowerTodos.url,
  };

  useEffect(() => {
    if (borrowerLoanApplicationsStore.isLoanDetailsSuccess === false) {
      push(miscellaneousRoutesConfig.notFound.url);
      borrowerLoanApplicationsStore.setLoanDetailsSuccess(null);
    }
  }, [borrowerLoanApplicationsStore.isLoanDetailsSuccess]);

  useEffect(() => {
    const handleTabChange = (e: Event) => {
      const { detail } = e as CustomEvent<{ loanId: number; tab: string }>;
      if (String(detail?.loanId) !== String(loanId)) return;
      const url = tabRouteUrls[detail?.tab];
      if (url && pathname.indexOf(url) === -1) {
        push(url);
      }
    };

    window.addEventListener(
      'platform-ui:borrower-loan:tab-change',
      handleTabChange
    );
    return () => {
      window.removeEventListener(
        'platform-ui:borrower-loan:tab-change',
        handleTabChange
      );
    };
  }, [loanId, pathname, push, borrowerLoanRoutesConfig]);

  useEffect(() => {
    let matchedTab: string | undefined;
    Object.entries(tabRouteUrls).forEach(([tab, url]) => {
      if (pathname.indexOf(url) !== -1) {
        matchedTab = tab;
      }
    });

    if (matchedTab) {
      window.dispatchEvent(
        new CustomEvent('platform-ui:borrower-loan:sync-tab', {
          detail: { loanId: Number(loanId), tab: matchedTab },
        })
      );
    }
  }, [loanId, pathname, borrowerLoanRoutesConfig]);

  useEffect(() => {
    borrowerLoanApplicationsStore.fetchLoanApplicationDetails(loanId);
    documentStore.loanTodosStore.fetchTodos(
      Number(loanId),
      LoanProcess.UNDERWRITING
    );
  }, [loanId]);

  useEffect(() => {
    if (loanDetails?.loanId) {
      loanCommunicationStore
        .fetchUserConversations([
          {
            type: FilterType.LOAN_ID,
            value: loanDetails.loanId,
          },
        ])
        .then(x => {
          if (x?.totalCount) {
            const y = x.conversationsData?.filter(cData => {
              return cData.chatRoomType == ChatRoomType.NEW_LOAN_REQUEST;
            })[0];
            if (y?.conversationSid) {
              setConversationSidToUse(y.conversationSid);
              setDoesUserHasAnyConversations(true);
            }
          }
        });
      loanCommunicationStore.fetchUnreadMessagesCount(loanId);
    }
  }, [loanDetails?.loanId]);

  if (!loanDetails) {
    return null;
  }

  const handleOpenConversationsDialog = () => {
    if (conversationSidToUse) {
      push(
        communicationRoutesConfig
          .loanApplicationConversations(loanId)
          .children.conversation(conversationSidToUse).url,
        { from: pathname }
      );
    } else {
      push(communicationRoutesConfig.loanApplicationConversations(loanId).url, {
        from: pathname,
      });
    }
  };

  return (
    <Box my={3}>
      <Box display="flex" flexDirection="column" mb={3}>
        <Box mb={1.5}>
          <BreadcrumbsNew
            breadCrumbItems={breadCrumbItems}
            separator={<span>&#187;</span>}
          />
        </Box>
      </Box>
      <Box display="flex">
        <Box flexGrow={1}>
          <BorrowerLoanApplicationDetailsChildRoutes isNew={true} />
        </Box>
      </Box>
      <BorrowerLoanDetailsFAB
        showConversations={doesUserHasAnyConversations}
        loanCommunicationStore={loanCommunicationStore}
        onOpenConversations={handleOpenConversationsDialog}
      />
    </Box>
  );
});
